import { CLASSY_ATTR, CLASSY_HREF } from './crawler.constants'
import CrawlerElement from './crawler.element'
import { ModalSDK } from '../modal/modal.types'
import { ComponentBase, ConfigResponse } from '../config/config.types'
import { getComponentSectionByType } from '../config/config.utils'
import { Configs } from '../config/config.service'
import HostPageEventInstrumentation from '../host-page-events/host-page-event.instrumentation'
import {
  DEFAULT_INLINE_GRID_DECISION,
  InlineGridDecision,
  resolveInlineGridDecision,
} from '../inline-grid/inline-grid-decision'
import type { InlineGridRenderers } from './crawler.element'

interface MatchedClassyElement {
  el: HTMLElement
  campaignId: string
  campaignConfig: ConfigResponse
  config?: ComponentBase
  featureFlags: ConfigResponse['featureFlags']
}

interface CrawlerProps {
  win: Window
  modalSDK: ModalSDK
  configs: Promise<Configs>
  hostPageEventInstrumentation?: HostPageEventInstrumentation
  inlineGridDecision?: InlineGridDecision
  inlineGridRenderers?: InlineGridRenderers
  pgdid?: string
}

export default class Crawler {
  private props: CrawlerProps
  private elements: CrawlerElement[] = []
  private destroyed = false

  constructor(props: CrawlerProps) {
    this.props = props
    this._initElements()
  }

  public destroy(): void {
    if (this.destroyed) {
      return
    }

    this.destroyed = true

    this.elements.forEach((el) => {
      if (typeof el.destroy === 'function') {
        el.destroy()
      }
    })
    this.elements = []
  }

  private async _initElements(): Promise<void> {
    this._initOrgElements()
    this._initClassyElements()
  }

  // Handle org. defined anchor elements with `?campaign` url parameter
  private _initOrgElements = () => {
    const els: NodeListOf<HTMLAnchorElement> = document.querySelectorAll(`a[href*=${CLASSY_HREF}]`)

    els.forEach((el) => {
      const href = el.getAttribute('href')

      if (!href) {
        return
      }

      const campaignId = this._extractCampaignId(href)

      if (!campaignId) {
        return
      }

      this.elements.push(
        new CrawlerElement({
          el,
          campaignId,
          config: null,
          hostPageEventInstrumentation: this.props.hostPageEventInstrumentation,
          inlineGridDecision: DEFAULT_INLINE_GRID_DECISION,
          inlineGridRenderers: this.props.inlineGridRenderers,
          win: this.props.win,
          modalSDK: this.props.modalSDK,
        })
      )
    })
  }

  private _extractCampaignId = (url: string): string | null => {
    // Check if the string starts directly with "campaign="
    if (url.startsWith(`${CLASSY_HREF}=`)) {
      return url.split('=')[1] || null
    }

    // Check if the string starts with "/?" or just "?"
    if (url.startsWith('/?') || url.startsWith('?')) {
      url = 'https://placeholder.com' + url
    }

    // Parse the URL and attempt to retrieve the 'campaign' parameter
    try {
      const urlObj = new URL(url)
      return urlObj.searchParams.get(CLASSY_HREF)
    } catch (_) {
      return null
    }
  }

  // Handle classy elements
  private _initClassyElements = async () => {
    const els: NodeListOf<HTMLElement> = document.querySelectorAll(`[${CLASSY_ATTR}]`)

    if (!els.length) {
      return
    }

    const configs = await this.props.configs
    const matchedElements: MatchedClassyElement[] = []

    els.forEach((el) => {
      const id = el.getAttribute('id')
      const campaignId = el.getAttribute(CLASSY_ATTR)

      if (!id || !campaignId) {
        return
      }

      const config = configs[campaignId]
      if (!config) {
        return
      }

      const elementConfig = this.getElementConfig(id, config)

      matchedElements.push({
        el,
        campaignId,
        campaignConfig: config,
        config: elementConfig,
        featureFlags: config.featureFlags,
      })
    })

    const inlineGridDecision =
      this.props.inlineGridDecision ||
      resolveInlineGridDecision({
        featureFlagSets: matchedElements
          .filter(({ config }) => config?.type === 'inline-donation-grid')
          .map(({ featureFlags }) => featureFlags),
      })

    matchedElements.forEach(({ el, campaignId, campaignConfig, config }) => {
      this.props.modalSDK.setConfig(campaignConfig)

      if (!config) {
        return
      }

      this.elements.push(
        new CrawlerElement({
          el,
          config,
          campaignId,
          hostPageEventInstrumentation: this.props.hostPageEventInstrumentation,
          inlineGridDecision,
          inlineGridRenderers: this.props.inlineGridRenderers,
          inlineGridProfile: campaignConfig.inlineGridProfile,
          organizationName: campaignConfig.organizationName,
          pgdid: this.props.pgdid,
          win: this.props.win,
          modalSDK: this.props.modalSDK,
        })
      )
    })
  }

  private getElementConfig = (id: string, config: ConfigResponse): ComponentBase | undefined => {
    const elementConfig = config.components[id]

    /**
     * For the inline-donation-grid component, we want to use
     * the non-visual props from the checkout-form component.
     */
    if (elementConfig?.type === 'inline-donation-grid') {
      const checkoutDonationFormSection = getComponentSectionByType(
        'checkout',
        'donation-form',
        config
      )

      if (checkoutDonationFormSection) {
        return {
          ...elementConfig,
          props: {
            ...elementConfig.props,
            minimumDonationAmount: config.minimum_donation_amount,
            donationAmounts: checkoutDonationFormSection.props.donationAmounts,
            donationFrequencies: checkoutDonationFormSection.props.donationFrequencies,
            hideRecurringEndDate: config.intelligentAsksConfig?.is_enabled
              ? true
              : checkoutDonationFormSection.props.hideRecurringEndDate,
            recurringAmounts: checkoutDonationFormSection.props.recurringAmounts,
            currencies: config.currency,
            orgTags: config.orgTags,
            showRecurringUpsell:
              config.intelligentAsksConfig?.is_enabled &&
              config.iaa.status === 200 &&
              config.iaa.data.showRecurringUpsell
                ? true
                : false,
          },
        } as ComponentBase
      }
    }

    return elementConfig
  }
}
